import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { salaryHistoryApi } from '@/services';
import type {
  EmployeeSalaryHistory,
  SalaryHistoryCreatePayload,
} from '@/types/payroll';

interface SalaryHistoryState {
  history: EmployeeSalaryHistory[];
  salaryAt: any | null;
  loading: boolean;
  saving: boolean;
  error: any | null;
}

const initialState: SalaryHistoryState = {
  history: [],
  salaryAt: null,
  loading: false,
  saving: false,
  error: null,
};

export const fetchHistory = createAsyncThunk(
  'salaryHistory/fetchHistory',
  async (employeeId: number | string, { rejectWithValue }) => {
    try {
      const response = await salaryHistoryApi.getAll(employeeId);
      const data = response.data?.data;
      // A bare `|| []` made a malformed/error body indistinguishable from a
      // genuinely empty history, so a schema change silently blanked the table.
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.history)) return data.history;
      throw new Error('Unexpected salary history response shape');
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createHistory = createAsyncThunk(
  'salaryHistory/createHistory',
  async ({ employeeId, payload }: { employeeId: number | string; payload: SalaryHistoryCreatePayload }, { dispatch, rejectWithValue }) => {
    try {
      const response = await salaryHistoryApi.create(employeeId, payload);
      await dispatch(fetchHistory(employeeId));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchSalaryAt = createAsyncThunk(
  'salaryHistory/fetchSalaryAt',
  async ({ employeeId, params }: { employeeId: number | string; params: { field: string; date: string } }, { rejectWithValue }) => {
    try {
      const response = await salaryHistoryApi.salaryAt(employeeId, params);
      return response.data?.data ?? response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const salaryHistorySlice = createSlice({
  name: 'salaryHistory',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.history = action.payload;
      })
      .addCase(fetchHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createHistory.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createHistory.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createHistory.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(fetchSalaryAt.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalaryAt.fulfilled, (state, action) => {
        state.loading = false;
        state.salaryAt = action.payload;
      })
      .addCase(fetchSalaryAt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = salaryHistorySlice.actions;
export default salaryHistorySlice.reducer;
